/**
 * ChatMessage Component
 *
 * Displays individual chat messages with different styles for user and assistant.
 */

import React from 'react';

import { MarkdownRenderer } from './MarkdownRenderer';

import type { ChatMessage as ChatMessageType } from '../../hooks/useAIChat';

interface ChatMessageProps {
  message: ChatMessageType;
}

/**
 * Format timestamp for display
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * ChatMessage component
 *
 * Renders user messages aligned right with primary color background.
 * Renders assistant messages aligned left with gray background and markdown support.
 */
export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%]">
          <div className="rounded-2xl rounded-br-md px-4 py-2.5 shadow-sm dark:shadow-lg dark:shadow-black/30 bg-primary-600 dark:bg-[#1B4965]">
            <p className="text-sm whitespace-pre-wrap text-white">{message.content}</p>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-400/70 mt-1 text-right">
            {formatTime(message.timestamp)}
          </p>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%]">
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 dark:bg-white/10 dark:backdrop-blur-sm flex items-center justify-center shadow-sm dark:shadow-lg dark:shadow-black/20">
            {/* Light mode image */}
            <img
              src="/assets/medsuite-ai-sillouette.png"
              alt="AI"
              className="w-5 h-5 object-contain dark:hidden"
            />
            {/* Dark mode image */}
            <img
              src="/assets/medsuite-ai-sillouette-darkmode.png"
              alt="AI"
              className="w-5 h-5 object-contain hidden dark:block"
            />
          </div>
          <div className="flex-1">
            <div className="rounded-2xl rounded-tl-md px-4 py-2.5 shadow-sm dark:shadow-lg dark:shadow-black/30 bg-gray-100 dark:bg-[#0F4C5C]">
              <div className="text-sm text-gray-800 dark:text-[#EAF6F8]">
                {message.content ? (
                  <MarkdownRenderer content={message.content} isStreaming={message.isStreaming} />
                ) : message.isStreaming ? (
                  <span className="inline-block w-2 h-4 bg-primary-500 animate-pulse" />
                ) : null}
              </div>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-400/70 mt-1">
              {formatTime(message.timestamp)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
